import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import {
  SQUAD_TV_EXIT_DOUBLE_PRESS_WINDOW_MS,
  SQUAD_TV_EXIT_HINT_VISIBLE_MS,
} from '../constants/squadTvMode.constants';
import { shouldTriggerSquadTvDoublePress } from '../helpers/squadTvDoublePress.helper';

interface UseSquadTvExitParams {
  enabled: boolean;
  onExit: () => void;
}

interface UseSquadTvExitReturn {
  handleBannerPress: () => void;
  showExitHint: boolean;
}

export function useSquadTvExit({ enabled, onExit }: UseSquadTvExitParams): UseSquadTvExitReturn {
  const lastBannerPressAtRef = useRef<number | null>(null);
  const [showExitHint, setShowExitHint] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setShowExitHint(false);
      lastBannerPressAtRef.current = null;
      return;
    }

    setShowExitHint(true);
    const timerId = setTimeout(() => {
      setShowExitHint(false);
    }, SQUAD_TV_EXIT_HINT_VISIBLE_MS);

    return () => {
      clearTimeout(timerId);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || Platform.OS !== 'web') {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onExit();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, onExit]);

  const handleBannerPress = useCallback(() => {
    const nowMs = Date.now();
    if (
      shouldTriggerSquadTvDoublePress(
        lastBannerPressAtRef.current,
        nowMs,
        SQUAD_TV_EXIT_DOUBLE_PRESS_WINDOW_MS,
      )
    ) {
      lastBannerPressAtRef.current = null;
      onExit();
      return;
    }
    lastBannerPressAtRef.current = nowMs;
  }, [onExit]);

  return { handleBannerPress, showExitHint };
}
